import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import AnimatedLogo from '@/components/ui/AnimatedLogo';
import { Label } from '@/components/ui/label';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/context/Auth';
import { AUTH_ROUTES, getAuthRedirectUrl, mapAuthError } from '@/utils/auth';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { requestPasswordReset } = useAuth();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    const error = await requestPasswordReset(
      email,
      getAuthRedirectUrl(AUTH_ROUTES.resetPassword)
    );
    setIsSubmitting(false);

    if (error) {
      const mappedError = mapAuthError(error, 'forgot_password');
      toast({
        title: t('forgot_password.toast.error.title.translation', 'Error'),
        description: t(mappedError.translationKey, mappedError.fallback),
      });
      return;
    }

    setIsSubmitted(true);
  };

  return (
    <div className="min-h-screen-dynamic flex items-center justify-center p-4 relative bg-background">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl w-full">
        <div className="flex flex-row space-x-6 justify-center items-center">
          <AnimatedLogo size={90} />
          <Label className="text-5xl md:text-8xl text-foreground">dialup</Label>
        </div>
        <div className="flex flex-col w-full items-center justify-center md:items-start p-2">
          <div className="flex flex-col justify-center md:justify-start p-10 space-y-4 w-full max-w-md">
            <h2 className="text-2xl font-bold text-foreground">
              {t('forgot_password.title.translation', 'Reset your password')}
            </h2>
            <p className="text-foreground">
              {t(
                'forgot_password.description.translation',
                'Enter your email and we will send you a password reset link.'
              )}
            </p>
            {isSubmitted ? (
              <p className="text-foreground text-sm">
                {t(
                  'forgot_password.success_message.translation',
                  'If an account exists for this email, a password reset link has been sent.'
                )}
              </p>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <Input
                  id="forgot-password-email"
                  type="email"
                  placeholder={t(
                    'forgot_password.email.placeholder.translation',
                    'Email'
                  )}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="!bg-card !text-foreground !placeholder:text-muted-foreground"
                  required
                  disabled={isSubmitting}
                />
                <Button
                  type="submit"
                  variant="gradient"
                  className="justify-center"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? t(
                        'forgot_password.submit.loading.translation',
                        'Sending...'
                      )
                    : t(
                        'forgot_password.submit.default.translation',
                        'Send reset link'
                      )}
                </Button>
              </form>
            )}
            <Button
              type="button"
              variant="link"
              className="!text-foreground hover:!text-foreground/80 justify-start px-0"
              onClick={() => navigate(AUTH_ROUTES.login)}
            >
              {t('forgot_password.back_to_login.translation', 'Back to Login')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
